import { Col, Row, Spinner } from "react-bootstrap";
import SentimentIndicator from "../../../components/SentimentIndicatorSelector";
import {  Survey, UpdateSurveyDetail } from "../types";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ErrorTopicDto } from "../../topics/types";
import { changeIAValuesSurveyDetail } from "../api/updateSurveyDetail";
import AlertMessage from "../../../components/AlertMessage";
import { useQueryClient } from '@tanstack/react-query';
import { formatDateDisplay } from "../../../utils/dateUtils";
import { useCurrentClient } from "../../auth/api/getCurrentClient";
import { GenericSaveButton } from "../../../components/common/GenericSaveButton";
import GenerateAlertSelector from "../../../components/GenerateAlertSelctor";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import TopicsSection from "../../topics/components/TopicsSection";
import { useAlertAIHandler } from "../../../hooks/useAlertForKanbanState";
import ModalGlobal from "../../../components/AppModal/ModalGlobal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PERMISSIONS } from "../../../constants/permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface SurveyDetailProps {
    survey: Survey;
    onGoBackClick: () => void;
}

export default function SurveyDetail({ survey, onGoBackClick }: SurveyDetailProps) {
    const navigate = useNavigate()
    
    const queryClient = useQueryClient();
    const { data: currentClient } = useCurrentClient();
    const { t } = useTranslation(['dashboard', 'common']);
    const [editing, setEditing] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [topics, setTopics] = useState(survey.topics);
    const [updateSurveyDetail, setUpdateSurveyDetail] = useState<UpdateSurveyDetail>({
        sentimentAI: survey.sentimentAI,
        alertAI: survey.alertAI,
        messageAI: survey.messageAI,
        recommendationAI: survey.recommendationAI,
        topics: survey.topics,
        originalAlertAI: survey.originalAlertAI,
        originalSentimentAI: survey.originalSentimentAI
    });
    
    const currentUserPermissions = useSelector((state: RootState) => state.currentUserInfo.data.permissions);

    const [searchParams] = useSearchParams();
    const from = searchParams.get('from');
    const surveyId = searchParams.get('survey');
    
    const {
        handleAlertAI,
        handleTopicAlertAI,
        handleDeleteAlert,
        showModalDeleteAlert,
        alert, hideAlert,showAlert, isVisible,
    } = useAlertAIHandler({
            state: { ...updateSurveyDetail, topics, hasAlert: survey.hasAlert, alertStatus: survey.alertStatus, alertId: survey.alertId },
            setAlertAI: (v) => setUpdateSurveyDetail({ ...updateSurveyDetail, alertAI: v || 0 }),
            setTopicAlertAI: (i, v) => {
                const newTopics = [...topics];
                newTopics[i] = { ...newTopics[i], alertAI: parseInt(v) };
                setTopics(newTopics);
            },
            onDeleteAlertConfirmed: () => setUpdateSurveyDetail(prev => ({ ...prev, deleteAlert: true })),
            fromLocation: true,
        });


    const handleCancel = () => {
		if (from == 'alert') {
            navigate(`/alertes?survey=${surveyId}`);
		} else {
            setUpdateSurveyDetail({
                sentimentAI: survey.sentimentAI,
                alertAI: survey.alertAI,
                messageAI: survey.messageAI,
                recommendationAI: survey.recommendationAI,
                topics: survey.topics,
                originalAlertAI: survey.originalAlertAI,
                originalSentimentAI: survey.originalSentimentAI
            });
            setTopics(survey.topics)
            setEditing(false);
        }
    }

    const saveSurveyDetail = async () => {
        const newErrors: { alertAI?: string, sentimentAI?: string, topics?: ErrorTopicDto[] } = {};
        setLoading(true);
        if (currentClient && updateSurveyDetail.alertAI < currentClient?.alertMinimumValue) {
            newErrors.alertAI = t("common:form.valueMustBeGreatherOrEqualThan", { num: currentClient?.alertMinimumValue });
        }

        if (currentClient && updateSurveyDetail.alertAI > currentClient?.alertMaximumValue) {
            newErrors.alertAI = t("common:form.valueMustBeLessOrEqualThan", { num: currentClient?.alertMaximumValue });
        }

        const payload: UpdateSurveyDetail = {
            ...updateSurveyDetail,
            topics: topics,
            alertId: survey.alertId ?? null,
        };
        try {
            await changeIAValuesSurveyDetail(survey.id, payload);
            await queryClient.invalidateQueries({
                queryKey: ['survey', survey.id],
            });
            
            await queryClient.invalidateQueries({
                queryKey: ['alertSurvey'],
                exact: false,
                refetchType: 'all'
            });
            
            if (from == 'alert') {
                navigate(`/alertes?survey=${surveyId}`);
            } else {
                setEditing(false);
                showAlert({
                    message: t("survey:detail.modalShowDetailAlertUpdateSuccesfully"),
                    type: 'success',
                    duration: 8000
                });
                setLoading(false);                
            }

        } catch (error) {
            const apiError = error as ApiErrorResponse;
            const apiErrorCode = (apiError as any)?.response?.data?.code ?? apiError?.code;
            const apiErrorMessage = (apiError as any)?.response?.data?.message ?? apiError?.message;
            if (apiErrorCode === "ERROR_SAVE_SURVEY_ALERT_STATE") {
                showAlert({
                    message: apiErrorMessage,
                    type: 'warning',
                    duration: 8000
                });
                setLoading(false);
                return;
            }

            showAlert({
                message: t("survey:detail.modalShowDetailSurveyUpdateError"),
                type: 'danger',
                duration: 8000
            });
            setLoading(false);
        }
    }
    return (
        
        <div>
            <ModalGlobal
                show={showModalDeleteAlert}
                onClose={() => handleDeleteAlert(false)}
                body={t("survey:modal.modalDeleteAlertTitle", { alertId: survey.alertId })}
                onSave={() => handleDeleteAlert(true)}
                saveText={t("common:buttons.buttonYes")}
                closeText={t("common:buttons.buttonNo")}
                isLoading={false}
            />
            {loading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={12} className="d-flex justify-content-end mb-3">
                {editing ? (
                    <GenericSaveButton
                        onClick={saveSurveyDetail}
                        onCancelClick={handleCancel}
                        disabled={loading}
                    />
                    ) : (
                    <>
                        <GenericSaveButton
                            onClick={() => setEditing(true)}
                            onCancelClick={onGoBackClick}
                            cancelLabel={t("common:buttons.buttonBack")}
                            saveLabel={t("common:buttons.buttonEdit")}
                            showSaveButton={currentUserPermissions.length > 0 && currentUserPermissions.includes(PERMISSIONS.SURVEYS_PATIENTS.EDIT)}
                            className="ms-2"
                        />
                    </>
                )}
            </Col>

                <Col xs={12} sm={6} md={6}>
                    <div className="flex-column">
                        <div className="mb-3">
                            <label>{t("dashboard:historicSurveyDetail.fieldSurveyId")}:</label>
                            <input type="text" className="form-control" value={survey.id} style={{ maxWidth: '300px' }} disabled />
                        </div>
                        <div className="mb-3">
                            <label>NPS:</label>
                            <input type="text" className="form-control" value={survey.nps ?? ""} style={{ maxWidth: '300px' }} disabled />
                        </div>
                        <div className="mb-3 " style={{ maxWidth: '300px' }}>
                             <label>{t("dashboard:historicSurveyDetail.fieldCommentFeeling")}:</label>
                            <SentimentIndicator
                                value={updateSurveyDetail.sentimentAI ?? null}
                                onChange={(val) => { if (val !== null) setUpdateSurveyDetail({ ...updateSurveyDetail, sentimentAI: val }); }}
                                disabled={!editing}
                                minValue={currentClient?.sentimentMinimumValue}
                                maxValue={currentClient?.sentimentMaximumValue}
                            />
                            {(survey.originalSentimentAI != null && updateSurveyDetail.sentimentAI != survey.originalSentimentAI) && (
                                <p style={{ color: 'green', marginTop: '10px' }}>{t("survey:detail.modalShowDetailSurveyAISentimentGenerated")} {updateSurveyDetail.originalSentimentAI}</p>
                            )}
                        </div>
                    </div>
                </Col>

                <Col xs={12} sm={6} md={6}>
                    <div className="flex-column">
                        <div className="mb-3">
                            <label>{t("dashboard:historicSurveyDetail.fieldSurveyDate")}:</label>
                            <input type="text" className="form-control" value={formatDateDisplay(survey.createDate) ?? ""} style={{ maxWidth: '300px' }} disabled />
                        </div>
                        <div className="mb-3">
                            <label>{t("dashboard:historicSurveyDetail.fieldAnswerDate")}:</label>
                            <input type="text" className="form-control" value={formatDateDisplay(survey.answerDate) ?? ""} style={{ maxWidth: '300px' }} disabled />
                        </div>
                        <div className="mb-3" style={{ maxWidth: '300px' }}>
                            <label>{t("dashboard:historicSurveyDetail.fieldAlert")}:</label>
                            <GenerateAlertSelector
                                value={updateSurveyDetail.alertAI ?? null}
                                onChange={handleAlertAI}
                                disabled={!editing}
                                minValue={currentClient?.sentimentMinimumValue}
                                maxValue={currentClient?.sentimentMaximumValue}
                                neutralReference={currentClient?.neutralReference}
                                alertMinimumValue={currentClient?.alertMinimumValue}
                                alertMaximumValue={currentClient?.alertMaximumValue}
                            />
                            {survey.originalAlertAI != null &&
                                updateSurveyDetail.alertAI !== survey.originalAlertAI && (
                                    <p style={{ color: "green", marginTop: "10px" }}>
                                        {t("survey:detail.modalShowDetailSurveyAIAlertGenerated")}{" "}
                                        {updateSurveyDetail.originalAlertAI}
                                    </p>
                                )}
                        </div>
                    </div>
                </Col>

                <Col sm={12} md={12}>
                    <div className="mb-3">
                        <TopicsSection
                            topics={topics}
                            setTopics={setTopics}
                            editing={editing}
                            disabled={!editing}
                            newTopicLabel={t("survey:detail.modalShowDetailButtonNewTopic")}
                            onChangeAlertAI={handleTopicAlertAI}
                        />
                    </div>
                    <div className="mb-3">
                        <label>{t("dashboard:historicSurveyDetail.fieldComment")}:</label>
                        <textarea className="form-control" value={survey.comment ?? ""} disabled style={{ minHeight: 160 }} />
                    </div>
                    <div className="mb-3">
                        <label>{t("dashboard:historicSurveyDetail.fieldRecommendation")}:</label>
                        <textarea className="form-control" value={updateSurveyDetail.recommendationAI ?? ""} disabled={!editing} style={{ minHeight: 160 }}
                            onChange={(e) =>
                                setUpdateSurveyDetail({
                                    ...updateSurveyDetail,
                                    recommendationAI: e.target.value,
                                })
                            }/>
                    </div>
                </Col>

                {survey.extraFields.map((field, index) => (
                    field.value && <Col key={index} xs={12} md={field.type === "DobleLabel" ? 12 : 6} className="mb-3">
                        {/* Condición para DobleLabel */}
                        {field.type === "DobleLabel" ? (
                            <>
                                {/* Verificar si la descripción tiene el delimitador "***" */}
                                {field.description && field.description.includes("***") ? (
                                    <label>
                                        {/* Si field.value es "true", muestra la primera parte de la descripción */}
                                        {field.value === "no" ? (
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: field.description.split("***")[1] // Aplica HTML en la primera parte
                                                }}
                                            />
                                        ) : (
                                            <>
                                                {/* Muestra la segunda parte de la descripción con HTML */}
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: field.description.split("***")[0] // Aplica HTML en la segunda parte
                                                    }}
                                                />
                                            </>
                                        )}
                                    </label>
                                ) : (
                                    <label>{field.description}</label> // Si no tiene el delimitador "***", muestra la descripción completa
                                )}
                            </>
                        ) : (
                            <>
                                <label>{field.description || field.field}: </label>
                                <input
                                    type="text"
                                    value={field.value ?? ""}
                                    className="form-control"
                                    style={{ maxWidth: '300px' }}
                                    disabled
                                />
                            </>
                        )}
                    </Col>
                ))}
                <Col sm={12} md={12}>
                    <div className="mb-3">
                        <label>{t("dashboard:historicSurveyDetail.fieldMessageAI")}:</label>
                        <textarea className="form-control" value={updateSurveyDetail.messageAI ?? ""} disabled={!editing} style={{ minHeight: 160 }}
                            onChange={(e) =>
                                setUpdateSurveyDetail({
                                    ...updateSurveyDetail,
                                    messageAI: e.target.value,
                                })
                            } />
                    </div>
                </Col>
            </Row>
        </div>
    );
}
